/**
 * Error humanizer (Day 12).
 *
 * Maps backend error codes / messages to friendly user-facing copy.
 * Falls back to the raw message when no mapping is known, so we never
 * swallow useful info — but the common cases get nice prose.
 *
 * Usage:
 *   try { ... } catch (err) { setError(humanize(err)); }
 */

const MESSAGE_MAP: Array<[RegExp, string]> = [
  // Permissions
  [/platform_admin_required/, "You don't have permission for this action — platform admin only."],
  [/owner_required/, 'Only your organization owner can do this.'],
  [/cross_org_audit_blocked/, "You can only view your own organization's audit log."],
  [/cross_org_staff/, 'That staff member belongs to a different organization.'],
  [/not_authorized_to_request_for_staff/, "You can't request time off for that staff member."],
  [/not_authorized_to_decide/, "You can't approve or deny this request — your manager or owner can.'"],
  [/not_authorized_to_cancel/, "You can't cancel this request."],
  [/only_requestor_can_withdraw/, 'Only the staff member who submitted this can withdraw it.'],
  [/permission_denied:/, 'You don\'t have permission for this action.'],

  // Not found / state
  [/staff_not_found/, "We couldn't find that staff member."],
  [/request_not_found/, "We couldn't find that time-off request."],
  [/banner_not_found/, "We couldn't find that banner."],
  [/banner_not_found_or_not_draft/, 'That banner is already published or archived.'],
  [/org_not_found/, "We couldn't find that organization."],
  [/invalid_state_for_decision/, 'This request has already been decided.'],
  [/invalid_state_for_withdraw/, 'You can only withdraw requests that are still pending.'],
  [/invalid_state_for_cancel/, 'This request can no longer be cancelled.'],
  [/invalid_transition_target/, "Time-off can only move to 'approved' or 'denied'."],

  // Validation
  [/end_before_start/, 'End date must be on or after the start date.'],
  [/end_date_before_start_date/, 'End date must be on or after the start date.'],
  [/inconsistent_half_day_markers/, "AM and PM markers can't both apply to a single day."],
  [/auth_required/, 'Please sign in and try again.'],

  // Maintenance / platform
  [/maintenance_mode_active/, 'PharmaSync is in maintenance mode. Try again shortly.'],

  // Conflicts (calendar)
  [/Shift has conflicts:/, 'This shift has scheduling conflicts. Resolve them or override with a reason.'],

  // Coupons / discounts (0079, 0080)
  [/coupon_not_found/, "We couldn't find that coupon."],
  [/coupon_inactive/, 'That coupon is deactivated. Reactivate it or pick another.'],
  [/coupon_expired/, "That coupon's redemption window has closed."],
  [/coupon_max_redemptions_reached/, 'That coupon has hit its redemption limit.'],
  [/discount_required/, 'Enter a percent or amount for the discount.'],
  [/discount_one_kind_only/, "Use either a percent OR an amount — not both."],
  [/org_subscription_not_found/, "We couldn't find a subscription for that organization."],

  // Generic shapes
  [/cannot execute INSERT in a read-only transaction/, 'Server hiccup. Please try again.'],
];

export function humanize(err: unknown): string {
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Something went wrong.';

  for (const [pattern, friendly] of MESSAGE_MAP) {
    if (pattern.test(raw)) return friendly;
  }
  // Fallback: trim PG error prefix if present (e.g. "42501: foo" → "foo").
  return raw.replace(/^[A-Z0-9]{5}:\s*/, '');
}
